import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import {
  usePermissions,
  useHasPermission,
} from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import * as React from 'react';

import type {
  UpcomingVisitWidgetMFEProps,
  UpcomingVisitWidgetProps,
} from './UpcomingVisitWidget.types';

const UpcomingVisitWidget = React.memo(
  ({
    onLoad,
  }: Readonly<UpcomingVisitWidgetProps>): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const canViewUpcomingVisitWidget = useHasPermission('ModuleTrain_View_Any');

    const [RemoteComponent, setRemoteComponent] =
      React.useState<React.ComponentType<UpcomingVisitWidgetMFEProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    React.useEffect(() => {
      if (!canViewUpcomingVisitWidget) {
        onLoad?.();
        return;
      }

      const loadComponent = async (): Promise<void> => {
        try {
          const { default: Component } =
            await import('learning_mfe/UpcomingVisitWidget');
          setRemoteComponent(() => Component);
        } catch (error) {
          console.error('Error loading UpcomingVisitWidget:', error);
          setLoadError(error as Error);
          onLoad?.();
        }
      };

      loadComponent();
    }, [canViewUpcomingVisitWidget, onLoad]);

    if (!canViewUpcomingVisitWidget || loadError) {
      return null;
    }

    if (!RemoteComponent || !authData) {
      return null;
    }

    return (
      <React.Suspense
        fallback={
          <ContentLoader data-testid="UpcomingVisitWidget-content-loader" />
        }
      >
        <RemoteComponent
          msalInstance={instance}
          user={authData}
          permissions={permissions}
          onLoad={onLoad}
        />
      </React.Suspense>
    );
  }
);

UpcomingVisitWidget.displayName = 'UpcomingVisitWidget';

export { UpcomingVisitWidget };
